import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { Subscription } from 'rxjs';
import { ClaimService } from '../claim.service';
import { ReportClaimRequest } from '../report-claim-request';
import { ClaimFormMapper } from '../claim-form-mapper';
import { LoadingService } from '../../../shared/loading.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { ClaimCustomerComponent } from '../claim-customer/claim-customer.component';
import { ClaimRepairFacilityComponent } from '../claim-repair-facility/claim-repair-facility.component';
import { ClaimPicturesComponent } from '../claim-pictures/claim-pictures.component';

@Component({
  selector: 'report-a-claim',
  templateUrl: './report-a-claim.component.html'
})
export class ReportAClaimComponent implements OnDestroy {

    @ViewChild('stepper') stepper: MatStepper;
    @ViewChild('customer') customer: ClaimCustomerComponent;
    @ViewChild('repairFacility') repairFacility: ClaimRepairFacilityComponent;
    @ViewChild('pictures') pictures: ClaimPicturesComponent;

    public modalMissingFileRef: MatDialogRef<DialogComponent>;
    
    // Indicates if the form has been submitted.
    public isFormSubmitted = false;
    public claimServiceSubscription: Subscription;

    // Indicates if the form is being submitted.
    private isSubmitting = false;
    private missingFieldsError = false;

    constructor(private claimService: ClaimService, private loadingService: LoadingService, private dialog: MatDialog) { }

    ngOnDestroy(): void {
        if (this.claimServiceSubscription) {
            this.claimServiceSubscription.unsubscribe();
        }
    }

    public showErrors(index: number): boolean {
        return this.missingFieldsError && this.stepper?.selectedIndex === index;
    }

    public backClicked(): void {
        this.stepper?.previous();

        // Set the missing field error flag after switching to previous step.
        this.missingFieldsError = !this.stepper.selected.completed;
    }

    public nextClicked(): void {
        // Note: Next will only ever be clicked when index is 0 right now.
        if (this.stepper?.selectedIndex === 0) {

            // Validate customer form.
            this.customer?.validateForm();
            this.stepper.selected.completed = (this.customer?.formCompleted);
            this.missingFieldsError = !this.stepper.selected.completed;

            // If invalid form(s), do not continue to next step.
            if (this.missingFieldsError) return;

            // Check missing estimate is intentional. Then continue to next step.
            if (this.customer?.fileUpload.isMissingUpload) {
                this.confirmEstimateUpload();
            }
            else {
                this.nextStepRepairFacility();
                return;
            }
        }
        if (this.stepper?.selectedIndex === 1) {
            this.validateRepairFacilityForm();

            // No next step.
        }
    }

    private nextStepRepairFacility(): void {
        this.stepper?.next()

        this.validateRepairFacilityForm();

        // Mark as dirty as user has now navigated to the form and should be valiated next time.
        this.repairFacility.claimRepairFacilityForm.markAsDirty();
    }

    private validateRepairFacilityForm(): void {
        // If RF form is dirty (i.e. from pre-population, re-visiting form), run validation.
        // Else, gives the user a chance to populate first before validating.
        if (this.repairFacility.claimRepairFacilityForm.dirty) {
            this.repairFacility.validateForm();
            this.stepper.selected.completed = this.repairFacility?.formCompleted;
            this.missingFieldsError = !this.stepper.selected.completed;
        }
    }

    private confirmEstimateUpload(): void {
        this.showDialog('Estimate confirmation',
            'Please note we need cause of failure and a repair estimate to authorize a claim. Are you sure you want to continue without uploading the estimate?',
            'Cancel', 'Yes, Continue');
    }

    public showDialog(title: string, messageText: string, cancelButtonText: string, confirmationButtonText: string, showConfirmation = true): void {
        this.modalMissingFileRef = this.dialog.open(DialogComponent, {
            data: {
                title: title,
                bodyText: messageText,
                cancelButtonText: cancelButtonText,
                confirmationButtonText: confirmationButtonText
            }
        });
        this.modalMissingFileRef.afterClosed().subscribe(result => {
            if (result) this.nextStepRepairFacility();
        });
    }

    public submit(): void {
        if (this.validateAllRequiredData()) {
            this.loadingService.show();
            const estimateFile: File = this.customer.fileUpload.importFiles[0] ?? null;
            const pictureFiles: File[] = this.pictures?.fileUpload.importFiles ?? [];
            const request = this.buildRequest();
            this.saveClaim(request, estimateFile, pictureFiles);
        }
        else {
            this.missingFieldsError = true;
        }
    }

    private validateAllRequiredData(): boolean {
        if (this.stepper?.selectedIndex === 1) {
            this.repairFacility.validateForm();
            this.missingFieldsError = !this.stepper.selected.completed;
        }
        return (this.customer?.formCompleted && this.repairFacility?.formCompleted);
    }

    private saveClaim(request: ReportClaimRequest, estimateFile: File, pictureFiles: File[]) {
        if (request === null) {
            alert('There was an error submitting the claim. Please try again.');
            return;
        }
        
        // If the form is already submitting, don't start a new request.
        if (this.isSubmitting) return;

        this.isSubmitting = true;
        this.claimServiceSubscription = this.claimService.saveReportAClaim(estimateFile, pictureFiles, request)
            .subscribe({
                next: _ => {
                    this.loadingService.hide();
                    this.isFormSubmitted = true;
                    this.isSubmitting = false;
                },
                error: error => {
                    this.loadingService.hide();
                    alert('Error submitting');
                    this.isSubmitting = false;
                    throw (error);
                }
            });
    }

    public buildRequest(): ReportClaimRequest {
        let claimRequest = new ReportClaimRequest();
        ClaimFormMapper.mapCustomerForm(claimRequest, this.customer.claimCustomerForm);
        ClaimFormMapper.mapReportClaimForm(claimRequest, this.repairFacility.claimRepairFacilityForm);
        ClaimFormMapper.mapEstimateForm(claimRequest, this.customer.claimCustomerForm);
        
        return claimRequest;
    }

    /**
     * Resets the form to its initial state.
     * @see {@link FormUtility.reset}
     */
    public restart(): void {
        this.customer.reset();
        this.repairFacility.reset();
        this.pictures?.reset();

        this.isFormSubmitted = false;
        this.missingFieldsError = false;
        this.stepper.selectedIndex = 0;
    }
}
